/**
 * GMA-1778 / light caregiver UI — format device position / motion for cards.
 * Pure helpers (no React) so unit tests import the real functions.
 */

const has = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));

/**
 * GMA-1945 — user-visible copy for this card, in one place.
 * The repo carries no i18n runtime, so these are the extraction points if one
 * is added; nothing else in the card should inline these strings.
 */
export const DASH = "—";
export const NOT_MOVING_LABEL = "Not moving";
export const NOT_REPORTED_LABEL = "Not reported";
export const NO_RECENT_REPORT_HEADLINE = "No recent report from this device";
export const HOME_ADDRESS_FALLBACK_COPY =
  "Showing home-address coordinates, not a live device position. Speed, direction, elevation and GPS accuracy appear after the next MyCompass report from the client's device.";
export const STALE_FIX_SUFFIX = "not updated in over 24 hours";
// #644: shown instead of HOME_ADDRESS_FALLBACK_COPY when the card can render
// the last known readings below — the original copy promises those fields are
// hidden, which would contradict the tiles directly under it.
export const LAST_KNOWN_HEADER = "Last known device data";
export const LAST_KNOWN_POSITION_LABEL = "Last known position";
export const HOME_ADDRESS_FALLBACK_WITH_HISTORY_COPY =
  "Showing home-address coordinates, not a live device position. The readings below are from the device's last report and may be out of date.";

/** m/s → mph string, or null when unknown. */
export const formatSpeedMph = (mps) => {
  if (!has(mps) || Number(mps) < 0) return null;
  const mph = Number(mps) * 2.23693629;
  // Measured near-zero → "Not moving". Absent/null never reaches here (returns
  // null above) and must not be treated as stationary — phone/iPad coarse fixes
  // routinely omit speed while the client is walking (#754).
  if (mph < 0.5) return NOT_MOVING_LABEL;
  return `${mph.toFixed(1)} mph`;
};

/** meters → feet string. */
export const formatElevationFt = (meters) => {
  if (!has(meters)) return null;
  const ft = Number(meters) * 3.280839895;
  return `${Math.round(ft).toLocaleString()} ft`;
};

/**
 * GMA-1953 — above this vertical accuracy the altitude stops being a reading.
 *
 * GPS vertical accuracy is characteristically several times worse than
 * horizontal, because the satellite geometry is poorer in that axis, and it
 * degrades further indoors. A live staging fix measured 6.24 m horizontal
 * against 178.67 m vertical on the same row.
 *
 * 30 m is where the figure stops being able to answer any question a caregiver
 * would ask of it: a storey is roughly 3 m, so it cannot place a floor long
 * before this, and by 30 m (±98 ft) it can no longer separate a building from
 * the street outside it. The exact number is a product decision and is meant to
 * be revisited — what is not negotiable is that some ceiling exists, and that
 * nothing below renders a bare figure.
 */
export const ELEVATION_ACCURACY_CEILING_M = 30;
export const ELEVATION_IMPRECISE_LABEL = "Not precise enough";
const ELEVATION_HINT = "Height above sea level from the last GPS fix.";

/**
 * The elevation tile's value and hint, together, because the two must agree:
 * the value never asserts more precision than the vertical accuracy supports,
 * and the hint always carries the number a caregiver needs to judge it.
 *
 * Three states, and none of them is a bare figure:
 *   good      "595 ft ±10 ft"
 *   coarse    "Not precise enough", with the real error bar in the hint
 *   unknown   "595 ft (accuracy unknown)"
 */
export const buildElevationReading = (altitudeMeters, verticalAccuracyMeters) => {
  const feet = formatElevationFt(altitudeMeters);
  if (!feet) return { value: null, hint: ELEVATION_HINT };

  // Core Location's sentinel for an unavailable vertical accuracy is negative.
  // Read as a plain number it is smaller than any real accuracy, so it would
  // pass the ceiling check below and render "±-3 ft" as an excellent fix.
  const known = has(verticalAccuracyMeters) && Number(verticalAccuracyMeters) >= 0;
  if (!known) {
    return {
      value: `${feet} (accuracy unknown)`,
      hint: `${ELEVATION_HINT} The device did not report how accurate this altitude is, so it cannot be relied on.`,
    };
  }

  const accM = Number(verticalAccuracyMeters);
  const accFt = roundUncertainty(accM * 3.280839895);
  if (accM > ELEVATION_ACCURACY_CEILING_M) {
    return {
      value: ELEVATION_IMPRECISE_LABEL,
      hint: `The last fix was accurate to ±${accFt.toLocaleString()} ft vertically — too coarse to place a floor or a hillside, so the altitude is not shown.`,
    };
  }

  return {
    value: `${feet} ±${accFt.toLocaleString()} ft`,
    hint: ELEVATION_HINT,
  };
};

/** Degrees → N / NE / … */
export const formatCardinal = (courseDegrees) => {
  if (!has(courseDegrees) || Number(courseDegrees) < 0) return null;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const course = ((Number(courseDegrees) % 360) + 360) % 360;
  const idx = Math.floor((course + 22.5) / 45) % 8;
  return dirs[idx];
};

export const formatDirection = (courseDegrees) => {
  const card = formatCardinal(courseDegrees);
  if (!card) return null;
  return `${card} (${Math.round(Number(courseDegrees))}°)`;
};

/**
 * Round an uncertainty for display without ever rounding a real one to zero.
 *
 * Below one unit, any fixed decimal count takes a small-but-genuine figure to
 * "0", which asserts an exact reading — the opposite of what an accuracy field
 * communicates. One significant digit keeps it honest and still reads cleanly.
 *
 * Shared by both accuracy formatters: they had the same problem and only one
 * of them had solved it, which is how ±0 ft shipped while ±0 m could not.
 */
const roundUncertainty = (n) => (n < 1 ? Number(n.toPrecision(1)) : Math.round(n));

export const formatAccuracyM = (meters) => {
  if (!has(meters) || Number(meters) <= 0) return null;
  return `±${roundUncertainty(Number(meters))} m`;
};

export const formatDeviceSource = (source) => {
  const map = {
    apple_iphone: "iPhone",
    apple_ipad: "iPad",
    apple_watch: "Apple Watch",
  };
  if (!source) return null;
  return map[String(source)] || String(source);
};

export const formatNetworkPath = (path) => {
  if (!path) return null;
  const map = { wifi: "Wi‑Fi", cellular: "Cellular", offline: "Offline", unknown: "Unknown" };
  return map[String(path)] || String(path);
};

export const formatDeviceLocationAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Date only ("Aug 25") for the age of a last report. */
export const formatDeviceReportDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * Motion metadata the server emits ONLY when the coordinates came from a device
 * fix. When the resolver fell back to the geocoded home address it nulls this
 * whole block together (clientWeatherService motionApplies), so the presence of
 * any one of these fields means a device report exists — which is the fact that
 * separates "reported and stationary" from "never reported".
 */
const DEVICE_REPORT_FIELDS = [
  "deviceLocationAt",
  "altitudeMeters",
  "speedMetersPerSecond",
  "courseDegrees",
  "horizontalAccuracyMeters",
  "verticalAccuracyMeters",
  "deviceSource",
  "networkPath",
];

const present = (v) => v !== null && v !== undefined && v !== "";

/** Did this location come from a device report at all? */
export const hasDeviceReport = (location) => {
  const loc = location || {};
  // A device fix with no metadata whatsoever still counts — the resolver told
  // us the coordinates are the device's.
  if (loc.source === "device" || loc.source === "device_stale") return true;
  return DEVICE_REPORT_FIELDS.some((field) => present(loc[field]));
};

/** Is this a device fix the resolver already considers aged out? */
export const isStaleDeviceFix = (location) => (location || {}).source === "device_stale";

/**
 * "No recent report from this device since Aug 25", or without the date when
 * the payload carries none. Never invents a date: the server currently nulls
 * deviceLocationAt in exactly the address-fallback case, so the age is often
 * genuinely unknown to this card.
 */
export const buildNoReportHeadline = (location) => {
  const loc = location || {};
  const when = formatDeviceReportDate(loc.lastDeviceReportAt ?? loc.deviceLocationAt);
  return when ? `${NO_RECENT_REPORT_HEADLINE} since ${when}` : NO_RECENT_REPORT_HEADLINE;
};

/**
 * Which of the three card states this location is in. One value drives both the
 * copy and the accent, so the two can never disagree — an amber "fault" accent
 * over "no GPS fix yet" copy would tell the caregiver two different things.
 */
export const DEVICE_LOCATION_STATE = {
  NO_COORDS: "no-coords",
  NO_REPORT: "no-report",
  REPORTED: "reported",
};

export const deviceLocationState = (location) => {
  if (!location?.hasLocation) return DEVICE_LOCATION_STATE.NO_COORDS;
  if (hasDeviceReport(location)) return DEVICE_LOCATION_STATE.REPORTED;
  // hasLocation is also true when the coordinates came ONLY from the geocoded
  // home address, which happens in two situations the caregiver must not see
  // as one: a device that reported and has since gone quiet (a fault, amber),
  // and a device that was never configured (not a fault, neutral).
  //
  // lastDeviceReportAt is the only field that separates them — the server
  // reports it ungated, whereas deviceLocationAt and every other motion field
  // are nulled by the same address fallback that produced this branch. Absent
  // it, the honest answer is the neutral state: accusing a device that never
  // existed of going quiet is the worse of the two errors.
  return present(location.lastDeviceReportAt)
    ? DEVICE_LOCATION_STATE.NO_REPORT
    : DEVICE_LOCATION_STATE.NO_COORDS;
};

/** Build the caregiver tile list from weather.location (device motion fields). */
export const buildDeviceLocationTiles = (location) => {
  const loc = location || {};
  const reported = hasDeviceReport(loc);
  const speedText = formatSpeedMph(loc.speedMetersPerSecond);
  // Only claim "Not moving" when Core Location actually measured a near-zero
  // speed. A null speed is the invalid/unavailable sentinel (and, on phone/iPad
  // coarse fixes, the everyday outcome) — rendering that as "Not moving" told
  // caregivers a walking client was stationary. Direction follows the same
  // rule: invent a heading only when speed itself says they are still.
  const speedMeasured =
    has(loc.speedMetersPerSecond) && Number(loc.speedMetersPerSecond) >= 0;
  const knownMoving = Boolean(speedText) && speedText !== NOT_MOVING_LABEL;
  const stationary = reported && speedMeasured && !knownMoving;

  const rows = [
    {
      key: "elevation",
      label: "Elevation",
      // GMA-1953: value and hint come from one helper so they cannot disagree
      // about how certain this reading is.
      ...buildElevationReading(loc.altitudeMeters, loc.verticalAccuracyMeters),
    },
    {
      key: "speed",
      label: "Speed",
      value: speedText,
      hint: "How fast the device was moving when the fix was taken.",
    },
    {
      key: "direction",
      label: "Direction",
      value:
        formatDirection(loc.courseDegrees) || (stationary ? NOT_MOVING_LABEL : null),
      hint: "Travel direction from the GPS course (not compass heading).",
    },
    {
      key: "accuracy",
      label: "GPS accuracy",
      value: formatAccuracyM(loc.horizontalAccuracyMeters),
      hint: "Horizontal uncertainty of the last fix.",
    },
    {
      key: "source",
      label: "Device",
      value: formatDeviceSource(loc.deviceSource),
      hint: "Which device reported this position (iPhone / Watch / iPad).",
    },
    {
      key: "network",
      label: "Network",
      value: formatNetworkPath(loc.networkPath),
      hint: "How the device was connected when it uploaded the fix (never an SSID).",
    },
  ];

  // A dash is legible on screen but reads as nothing to a screen reader, so
  // every tile also carries words for assistive technology.
  return rows.map((row) => ({
    ...row,
    value: row.value || DASH,
    ariaValue: row.value || NOT_REPORTED_LABEL,
  }));
};

/**
 * #644 — last known device readings for the no-report state.
 *
 * The server's `lastDeviceFix` block (cortex-be#1279) carries the stored
 * device columns with their own timestamp, ungated by the address fallback.
 * Mapping it onto the location field names lets the existing tile builder
 * render it, so the two grids can never drift apart in formatting rules.
 */
export const lastFixAsLocation = (fix) => {
  if (!fix) return null;
  return {
    deviceLocationAt: fix.at ?? null,
    altitudeMeters: fix.altitudeMeters ?? null,
    verticalAccuracyMeters: fix.verticalAccuracyMeters ?? null,
    speedMetersPerSecond: fix.speedMetersPerSecond ?? null,
    courseDegrees: fix.courseDegrees ?? null,
    horizontalAccuracyMeters: fix.horizontalAccuracyMeters ?? null,
    deviceSource: fix.deviceSource ?? null,
    networkPath: fix.networkPath ?? null,
  };
};

export const buildLastKnownTiles = (fix) =>
  buildDeviceLocationTiles(lastFixAsLocation(fix) || {});

/**
 * "Last known device data · Aug 28 at 10:17 AM" — or the bare header when the
 * timestamp is missing or unparseable. Never invents a date (same invariant
 * as buildNoReportHeadline).
 */
export const buildLastKnownHeader = (fix) => {
  const when = formatDeviceLocationAt(fix?.at);
  return when ? `${LAST_KNOWN_HEADER} · ${when}` : LAST_KNOWN_HEADER;
};

/**
 * react-app#725 — "Last known position · 40.71280, -74.00600 · Sep 2, 2:05 PM".
 *
 * The fix block (cortex-be#1279) carries the device's OWN latitude/longitude,
 * which in the no-report state is the only position on the page that is not
 * the home address. Null when either coordinate is missing or not finite —
 * older fix rows predate the fields, and the card then renders as before.
 * Five decimals, the precision IotEventHistorySection already uses; the
 * time comes from formatDeviceLocationAt and is never invented.
 */
export const buildLastKnownPosition = (fix) => {
  if (!fix || !has(fix.latitude) || !has(fix.longitude)) return null;
  return {
    coords: `${Number(fix.latitude).toFixed(5)}, ${Number(fix.longitude).toFixed(5)}`,
    when: formatDeviceLocationAt(fix.at),
  };
};
